import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, ArrowRight, ArrowLeft, Check, AlertCircle, Truck, Ruler, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import Breadcrumbs from '../components/Breadcrumbs';
import { findProduct, allProducts, businessInfo } from '../data/mock';
import { openWhatsApp, trackPhone } from '../lib/analytics';

const SITE = 'https://www.maderaselocote.com';

const ProductoDetalle = () => {
  const { slug } = useParams();
  const product = findProduct(slug);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-5">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-heading font-bold text-wood-900 mb-4">Producto no encontrado</h1>
          <p className="text-wood-600 mb-8">
            Puede que el enlace esté mal escrito o que el producto ya no esté en el catálogo.
          </p>
          <Link to="/productos">
            <Button className="bg-wood-900 hover:bg-wood-800 text-white rounded-full px-8">
              Ver todos los productos
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const canonical = `${SITE}/producto/${product.slug}`;
  // Se corta en el limite recomendado para meta description, sin partir palabras.
  const metaDescription = `${product.title} en Torreón, Coahuila. ${product.description}`
    .slice(0, 155)
    .replace(/\s+\S*$/, '');

  const quoteMessage = `Hola, me interesa cotizar: ${product.title} (${product.categoryName}). Cantidad y medidas: `;

  const related = allProducts
    .filter((p) => p.categoryId === product.categoryId && p.slug !== product.slug)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{`${product.title} | Maderas El Ocote - Torreón`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.title} | Maderas El Ocote`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {/* Sin JSON-LD de Product a proposito: Google exige que un Product
            declare offers, review o aggregateRating para ser valido, y aqui no
            hay precios publicos -se cotiza por WhatsApp- ni resenas por
            producto. Un Product incompleto solo genera "elemento no valido" en
            Search Console sin dar ningun beneficio, porque sin precio los
            resultados enriquecidos son inalcanzables de todos modos. La
            jerarquia la aporta BreadcrumbList y el negocio, LocalBusiness. */}
      </Helmet>

      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 pt-32 pb-24">
          <Breadcrumbs
            items={[
              { label: 'Productos', path: '/productos' },
              { label: product.categoryName, path: '/productos' },
              { label: product.title },
            ]}
          />

          <header className="mb-12">
            <p className="text-sm font-semibold text-wood-400 uppercase tracking-widest mb-3">
              {product.categoryName}
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-wood-900 leading-tight mb-6">
              {product.title}
            </h1>
            <p className="text-lg text-wood-600 leading-relaxed max-w-3xl">
              {product.description}
            </p>
          </header>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              {product.uses && product.uses.length > 0 && (
                <section>
                  <h2 className="text-xl font-heading font-bold text-wood-900 mb-4">Usos comunes</h2>
                  <ul className="space-y-3">
                    {product.uses.map((use, i) => (
                      <li key={i} className="flex items-start gap-3 text-wood-700">
                        <Check className="w-5 h-5 text-wood-400 flex-shrink-0 mt-0.5" />
                        <span>{use}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {product.specs && (
                <section>
                  <h2 className="text-xl font-heading font-bold text-wood-900 mb-4">Medidas disponibles</h2>
                  <div className="bg-wood-50 border border-wood-200/60 rounded-xl p-5">
                    <p className="text-wood-700">{product.specs}</p>
                  </div>
                </section>
              )}

              {product.availability && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 font-medium">{product.availability}</p>
                </div>
              )}

              {product.legalNotice && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 font-medium">{product.legalNotice}</p>
                </div>
              )}

              <section>
                <h2 className="text-xl font-heading font-bold text-wood-900 mb-4">Servicios sobre este material</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-5 rounded-xl border border-wood-200/60">
                    <Ruler className="w-5 h-5 text-wood-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-wood-900 text-sm mb-1">Cortes a medida y cepillado</h3>
                      <p className="text-sm text-wood-600">
                        Lo dimensionamos a las medidas de tu proyecto antes de entregarlo.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-5 rounded-xl border border-wood-200/60">
                    <Truck className="w-5 h-5 text-wood-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-wood-900 text-sm mb-1">Entrega coordinada</h3>
                      <p className="text-sm text-wood-600">
                        Torreón, Gómez Palacio, Lerdo y zonas cercanas de la Comarca Lagunera.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {related.length > 0 && (
                <section>
                  <h2 className="text-xl font-heading font-bold text-wood-900 mb-4">
                    {`Otros productos de ${product.categoryName}`}
                  </h2>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {related.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/producto/${item.slug}`}
                        className="group p-5 rounded-xl border border-wood-200/60 hover:border-wood-300 hover:bg-wood-50/50 transition-all duration-300"
                      >
                        <h3 className="font-semibold text-wood-900 text-sm mb-2 group-hover:text-wood-800">
                          {item.title}
                        </h3>
                        <ArrowRight className="w-4 h-4 text-wood-400 group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              <Link
                to="/productos"
                className="inline-flex items-center gap-2 text-wood-600 hover:text-wood-900 text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al catálogo
              </Link>
            </div>

            <aside>
              <div className="sticky top-24 space-y-5">
                <div className="p-7 rounded-2xl bg-wood-900 text-white">
                  <h2 className="font-heading font-bold text-white text-lg mb-2">
                    {`Cotiza ${product.title.toLowerCase()}`}
                  </h2>
                  <p className="text-sm text-white/60 mb-5">
                    Te respondemos con precio y disponibilidad para tu pedido.
                  </p>
                  <Button
                    onClick={() => openWhatsApp('producto_detalle', quoteMessage)}
                    className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-xl py-6 text-base"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Cotizar por WhatsApp
                  </Button>
                  <a
                    href={`tel:${businessInfo.phone}`}
                    onClick={trackPhone('producto_detalle')}
                    className="flex items-center justify-center gap-2 mt-3 text-sm text-white/70 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {businessInfo.phone}
                  </a>
                </div>

                <div className="p-6 rounded-2xl border border-wood-200/60 bg-wood-50">
                  <h2 className="font-heading font-semibold text-wood-900 text-sm mb-3">Ten a la mano</h2>
                  <ul className="space-y-2 text-sm text-wood-600">
                    {['Medidas que necesitas', 'Cantidad aproximada', 'Uso o aplicación', 'Fecha de entrega'].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-wood-300 mt-1.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductoDetalle;
